import {
	Alert,
	Box,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Snackbar,
	Table,
	Typography,
} from "@mui/joy";
import React from "react";
import { useRole } from "../Contexts/RoleContext";

interface SyncRun {
	qbo_sync_run_id: string;
	started_at: string;
	completed_at?: string;
	status: string;
	error_message?: string;
	proposals_synced?: number;
	invoices_synced?: number;
	duration_seconds?: number;
}

interface SyncStatus {
	latest?: SyncRun;
	nextSyncUtc: string;
	intervalMinutes: number;
}

export default function QboSyncAdmin() {
	const { isActuallyAdmin } = useRole();
	const [status, setStatus] = React.useState<SyncStatus | null>(null);
	const [history, setHistory] = React.useState<SyncRun[]>([]);
	const [loading, setLoading] = React.useState(true);
	const [error, setError] = React.useState<string | null>(null);

	const loadAll = React.useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const [statusRes, historyRes] = await Promise.all([
				fetch("/QboSync/GetStatus", { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" }),
				fetch("/QboSync/GetHistory", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ top: 200 }) }),
			]);

			const statusJson = await statusRes.json();
			const historyJson = await historyRes.json();

			if (statusJson.error) {
				setError(statusJson.error);
				return;
			}
			if (historyJson.error) {
				setError(historyJson.error);
				return;
			}

			setStatus(statusJson);
			setHistory(Array.isArray(historyJson) ? historyJson : []);
		} catch (e: any) {
			setError(e?.message ?? "Failed to load QBO sync data");
		} finally {
			setLoading(false);
		}
	}, []);

	React.useEffect(() => {
		if (isActuallyAdmin()) loadAll();
	}, [isActuallyAdmin, loadAll]);

	React.useEffect(() => {
		if (!isActuallyAdmin()) return;
		const interval = setInterval(loadAll, 60_000);
		return () => clearInterval(interval);
	}, [isActuallyAdmin, loadAll]);

	if (!isActuallyAdmin()) {
		return <Alert color="warning">Admin access required.</Alert>;
	}

	const latest = status?.latest;
	const statusColor = latest?.status === "Success" ? "success" : latest?.status === "Failed" ? "danger" : latest?.status === "Running" ? "warning" : "neutral";

	const formatDuration = (seconds?: number) => {
		if (seconds == null) return "—";
		if (seconds < 60) return `${seconds}s`;
		return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
	};

	/** DB and timer times are UTC; normalize before local display. */
	const formatUtc = (value?: string | null) => {
		if (!value) return "—";
		const s = String(value).trim();
		const iso = /[zZ]|[+-]\d{2}:\d{2}$/.test(s) ? s : `${s}Z`;
		return new Date(iso).toLocaleString(undefined, { timeZoneName: "short" });
	};

	return (
		<Box sx={{ overflowY: "auto" }}>
			<Typography level="h3" sx={{ mb: 2 }}>
				QBO Sync
			</Typography>

			{loading ? (
				<CircularProgress />
			) : (
				<>
					<Card sx={{ mb: 2 }}>
						<CardContent>
							<Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mb: 1 }}>
								<Chip color={statusColor} variant="soft">
									{latest?.status ?? "No runs yet"}
								</Chip>
								<Typography level="body-sm" sx={{ color: "text.tertiary" }}>
									Runs every {status?.intervalMinutes ?? 15} minutes (Azure Function timer, UTC schedule)
								</Typography>
							</Box>
							<Typography level="body-sm">
								<strong>Last sync started:</strong>{" "}
								{formatUtc(latest?.started_at)}
							</Typography>
							<Typography level="body-sm">
								<strong>Last sync completed:</strong>{" "}
								{latest?.completed_at ? formatUtc(latest.completed_at) : latest?.status === "Running" ? "In progress…" : "—"}
							</Typography>
							<Typography level="body-sm">
								<strong>Next scheduled sync:</strong>{" "}
								{formatUtc(status?.nextSyncUtc)}
							</Typography>
							{latest?.status === "Success" && (
								<Typography level="body-sm" sx={{ mt: 0.5 }}>
									Proposals: {latest.proposals_synced ?? 0} · Invoices: {latest.invoices_synced ?? 0} · Duration:{" "}
									{formatDuration(latest.duration_seconds)}
								</Typography>
							)}
							{latest?.status === "Failed" && latest.error_message && (
								<Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
									{latest.error_message}
								</Typography>
							)}
						</CardContent>
					</Card>

					<Typography level="title-md" sx={{ mb: 1 }}>
						Sync history
					</Typography>
					<Table stripe="even" hoverRow>
						<thead>
							<tr>
								<th>Started</th>
								<th>Completed</th>
								<th>Status</th>
								<th>Proposals</th>
								<th>Invoices</th>
								<th>Duration</th>
								<th>Error</th>
							</tr>
						</thead>
						<tbody>
							{history.length === 0 ? (
								<tr>
									<td colSpan={7} style={{ textAlign: "center", padding: "1rem" }}>
										No sync runs logged yet. Deploy the SQL script and run a sync.
									</td>
								</tr>
							) : (
								history.map((r) => (
									<tr key={r.qbo_sync_run_id}>
										<td>{formatUtc(r.started_at)}</td>
										<td>{r.completed_at ? formatUtc(r.completed_at) : "—"}</td>
										<td>
											<Chip
												size="sm"
												color={r.status === "Success" ? "success" : r.status === "Failed" ? "danger" : "warning"}
												variant="soft">
												{r.status}
											</Chip>
										</td>
										<td>{r.proposals_synced ?? "—"}</td>
										<td>{r.invoices_synced ?? "—"}</td>
										<td>{formatDuration(r.duration_seconds)}</td>
										<td style={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
											{r.error_message ?? ""}
										</td>
									</tr>
								))
							)}
						</tbody>
					</Table>
				</>
			)}

			<Snackbar open={!!error} color="danger" onClose={() => setError(null)}>
				{error}
			</Snackbar>
		</Box>
	);
}
